import React from 'react';
import { SessionboardEmbedWidget } from './embedWidget.jsx';
import { createRoot } from 'react-dom/client';
import { WIDGET_TYPES } from './constants.js';
import { definePayloadProperty, readDataSourceAttributes } from './utils/embed-host.js';
import cssText from './schedule/schedule.css?inline';
import ScheduleWidget from './schedule/ScheduleWidget.jsx';

class SessionboardShadowEmbed extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    /** @type {import('react-dom/client').Root | null} */
    this._root = null;
    /** @type {HTMLElement | null} */
    this._container = null;
  }

  connectedCallback() {
    if (this._root) return;

    const widgetType = this.getAttribute('widget-type') || WIDGET_TYPES.SCHEDULE;
    if (widgetType !== WIDGET_TYPES.SCHEDULE) {
      console.error('Invalid widget type', widgetType);
      return;
    }

    // Create and inject styles into shadow DOM
    const style = document.createElement('style');
    style.id = 'sessionboard-embed-styles';
    style.textContent = cssText;

    // Create container for React app
    const container = document.createElement('div');
    this._container = container;

    // Replace (not append) so re-inserting the element into the DOM doesn't
    // stack duplicate style/container nodes in the persistent shadow root.
    this.shadowRoot.replaceChildren(style, container);

    this._widgetType = widgetType;
    this._root = createRoot(container);
    this._render();
  }

  static get observedAttributes() {
    return ['language'];
  }

  // SB-9141: a language switch after mount refetches, so the chrome and the
  // container's dir/lang follow the newly resolved locale.
  attributeChangedCallback(name, oldValue, newValue) {
    if (this._root && oldValue !== newValue) this._render();
  }

  _render() {
    this._root.render(
      React.createElement(SessionboardEmbedWidget, {
        embedId: this.getAttribute('embed-id'),
        widgetType: this._widgetType,
        // SB-8809: per-instance language override; the API validates and falls
        // back to the embed's stored default (then English) when absent/junk.
        language: this.getAttribute('language'),
        ...readDataSourceAttributes(this),
        host: this,
        container: this._container,
        WidgetComponent: ScheduleWidget,
        shadowRoot: this.shadowRoot,
      })
    );
  }

  // SB-8704: without this, removing the element from the DOM never unmounted
  // the React root, so effect cleanups (including the 202 retry timer from
  // SB-8666) never ran — a removed embed kept polling async-data to the
  // 15-attempt cap.
  disconnectedCallback() {
    if (this._root) {
      this._root.unmount();
      this._root = null;
    }
    if (this._container) {
      this._container.remove();
      this._container = null;
    }
  }
}

// Register the custom element
definePayloadProperty(SessionboardShadowEmbed);

if (typeof window !== 'undefined' && !customElements.get('sessionboard-embed')) {
  customElements.define('sessionboard-embed', SessionboardShadowEmbed);
}
